import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { MfaRecoveryCodes, ReadUser } from '@tinct/core-api-models';
import { useRegenerateMfaRecoveryCodes } from '@tinct/core-api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { handleApiError } from '@tinct/web-form';
import { toast } from 'sonner';
import { useMfaFreshProofForm } from '../../hooks/use-mfa-fresh-proof-form';
import { MfaFreshProofFields } from './mfa-fresh-proof-fields';
import { RecoveryCodesList } from './recovery-codes-list';

type RegenerateRecoveryCodesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ReadUser;
};

/** Fresh proof first, then the new codes. The old ones are void as soon as the API answers. */
export const RegenerateRecoveryCodesModal = ({ open, onOpenChange, user }: RegenerateRecoveryCodesModalProps) => {
  const [recoveryCodes, setRecoveryCodes] = useState<MfaRecoveryCodes | null>(null);
  const { form, toRequest, allowCode } = useMfaFreshProofForm(user);

  const regenerate = useRegenerateMfaRecoveryCodes({
    onSuccess: (codes) => {
      toast.success('New recovery codes generated');
      setRecoveryCodes(codes);
    },
    onError: (error) => handleApiError(error, form),
  });

  const handleSubmit = form.handleSubmit((data) => {
    regenerate.mutate({ userId: user.id as string, data: toRequest(data) });
  });

  if (recoveryCodes) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Your new recovery codes</DialogTitle>
            <DialogDescription>The previous codes no longer work.</DialogDescription>
          </DialogHeader>
          <RecoveryCodesList codes={recoveryCodes.recoveryCodes} accountEmail={user.email} />
          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              I have saved my recovery codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form id="regenerate-recovery-codes-form" onSubmit={handleSubmit} className="contents">
          <FormProvider {...form}>
            <DialogHeader>
              <DialogTitle>Regenerate recovery codes</DialogTitle>
              <DialogDescription>
                You will get 10 new single-use codes and the current ones stop working. Confirm it
                is you first.
              </DialogDescription>
            </DialogHeader>

            <MfaFreshProofFields allowCode={allowCode} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" form="regenerate-recovery-codes-form" isLoading={regenerate.isPending}>
                Generate new codes
              </Button>
            </DialogFooter>
          </FormProvider>
        </form>
      </DialogContent>
    </Dialog>
  );
};
